// src/firebase/config.js
//
// Firebase initialization — reads all config from environment variables
// (see .env.example). Never hard-code API keys here.
//
// Phase 1: Secrets hardening.
// Phase 5: Offline-first persistence via IndexedDB.

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
} from 'firebase/firestore';

const requiredKeys = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

const missing = requiredKeys.filter((k) => !process.env[k]);
if (missing.length > 0 && process.env.NODE_ENV !== 'test') {
  // Don't throw — CRA replaces process.env at build time, so a missing var
  // is a configuration problem, not a runtime bug. Warn loudly in dev.
  // In tests we silence this so the harness can mock the module.
  // eslint-disable-next-line no-console
  console.warn(
    `[Al-Bayan] Missing Firebase env vars: ${missing.join(', ')}.\n` +
      'Copy .env.example to .env.local and fill in the values.',
  );
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const storage = getStorage(app);

// Phase 5: Offline-first. Use IndexedDB-backed persistent cache with
// multi-tab support so teachers working in low-connectivity madaris keep
// reading cached data while offline and writes are queued locally. The
// test environment (jsdom) does not expose IndexedDB, so fall back to
// the in-memory cache there to keep the Jest harness happy.
const cache =
  typeof window !== 'undefined' &&
  typeof window.indexedDB !== 'undefined' &&
  process.env.NODE_ENV !== 'test'
    ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    : memoryLocalCache();

export const db = initializeFirestore(app, { localCache: cache });

export default app;
